import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import Skeleton from '../../components/layout/Skeleton';
import { differenceInDays } from 'date-fns';
import { translateFilter, FilterProps, SCORE_THRESHOLDS } from './util';
import GaugeChart from '@/components/layout/GaugeChart';

type Props = {
  colorPalette: string[];
  filter: FilterProps;
};

type Periodo = 'dia' | 'semana' | 'mes';

type LinhaChave = {
  chave: string;
  avg_score: number | null;
  qtd_pontuacoes: number;
  [campoPeriodo: string]: any;
};

// Chaves são livres (definidas em cada fluxo), então só damos um tratamento
// especial à padrão; as demais só ganham a primeira letra maiúscula.
const humanizarChave = (chave: string) => {
  if (chave.toLowerCase() === 'nps') return 'NPS';
  return chave
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};

// Mesmo formato usado pelos outros gráficos da página (Desempenho, Comportamento
// do Atendente etc): data local por extenso, sem depender da granularidade.
const formatarData = (raw: string) => new Date(raw.substring(0, 10) + 'T00:00:00.000').toLocaleDateString();

export const GraficoNPS = ({ colorPalette, filter }: Props) => {
  const calcBestInterval = (): Periodo => {
    let d1 = filter.periodo![1];
    let d2 = filter.periodo![2];
    if (!d2) d2 = new Date().toISOString().substring(0, 10);

    const days = differenceInDays(d2, d1);
    if (days <= 7) return 'dia';
    if (days <= 31) return 'semana';
    return 'mes';
  };

  const [selectedPeriod, setSelectedPeriod] = useState<Periodo>(calcBestInterval);

  useEffect(() => {
    setSelectedPeriod(calcBestInterval());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.periodo]);

  const campoPeriodo = `data_criacao_${selectedPeriod}`;

  const q = useQuery(
    ['nps', 'por_chave_periodo', filter, selectedPeriod],
    async () => {
      const qs = new URLSearchParams();
      qs.append('facts', 'avg_score,qtd_pontuacoes');
      qs.append('dimensions', `chave,${campoPeriodo}`);
      qs.append('filter', translateFilter(filter));

      const res = await axios.get('/api/relatorios/chat_pontuacao?' + qs.toString());
      return res.data.result as LinhaChave[];
    },
    { refetchOnWindowFocus: false },
  );

  const [foco, setFoco] = useState<string | null>(null);

  const seletorPeriodo = (
    <HBox>
      {(['dia', 'semana', 'mes'] as Periodo[]).map((p) => (
        <Botao
          key={p}
          variant={selectedPeriod === p ? 'outline-primary' : 'outline-secondary'}
          onClick={() => setSelectedPeriod(p)}
        >
          {p === 'dia' ? 'Dia' : p === 'semana' ? 'Semana' : 'Mês'}
        </Botao>
      ))}
    </HBox>
  );

  if (q.isFetching) {
    return <Skeleton format="box" />;
  }

  const dados = (q.data ?? []).filter((row) => row.avg_score != null);
  const chaves = Array.from(new Set(dados.map((row) => row.chave)));
  const periodos = Array.from(new Set(dados.map((row) => row[campoPeriodo])));

  let conteudo: React.ReactNode;

  if (dados.length === 0) {
    conteudo = (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <p>Nenhum dado encontrado</p>
      </div>
    );
  } else if (periodos.length <= 1 && chaves.length <= 1) {
    // Um único período, uma única chave: gauge.
    const row = dados[0];
    conteudo = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <GaugeChart value={row.avg_score as number} thresholds={SCORE_THRESHOLDS} />
        <p style={{ fontStyle: 'italic', color: 'var(--tc-color-gray-500)' }}>
          {row.qtd_pontuacoes} {row.qtd_pontuacoes === 1 ? 'resposta' : 'respostas'} — {formatarData(row[campoPeriodo])}
        </p>
      </div>
    );
  } else if (periodos.length <= 1) {
    // Um único período, múltiplas chaves: barra por chave.
    const dadosBarra = dados.map((row) => ({
      chave: humanizarChave(row.chave),
      cor: colorPalette[chaves.indexOf(row.chave) % colorPalette.length],
      avg_score: row.avg_score as number,
      qtd_pontuacoes: row.qtd_pontuacoes,
    }));

    conteudo = (
      <HBox>
        <ResponsiveContainer height={220}>
          <BarChart data={dadosBarra} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="chave" />
            <YAxis domain={[0, 100]} />
            <Tooltip
              formatter={(value: any, name: string, props: any) =>
                name === 'avg_score' ? [`${Math.round(value)}% (${props.payload.qtd_pontuacoes} respostas)`, 'Pontuação média'] : value
              }
            />
            <Bar dataKey="avg_score" name="avg_score">
              {dadosBarra.map((entry) => (
                <Cell key={entry.chave} fill={entry.cor} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </HBox>
    );
  } else {
    // Mais de um período: trend por chave, uma linha por série.
    const porPeriodo = new Map<string, Record<string, any>>();
    for (const row of dados) {
      const raw = row[campoPeriodo];
      if (!porPeriodo.has(raw)) {
        porPeriodo.set(raw, { periodoRaw: raw, periodoFmt: formatarData(raw) });
      }
      porPeriodo.get(raw)![row.chave] = row.avg_score;
    }
    const dadosLinha = Array.from(porPeriodo.values()).sort((a, b) => a.periodoRaw.localeCompare(b.periodoRaw));

    conteudo = (
      <HBox>
        <ResponsiveContainer height={220}>
          <LineChart data={dadosLinha} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="periodoFmt" />
            <YAxis domain={[0, 100]} />
            <Tooltip
              itemSorter={(item: any) => -(item.value as number)}
              itemStyle={{ padding: 0, fontSize: '0.9rem' }}
              labelStyle={{ fontWeight: 'bold' }}
            />
            <Legend
              onMouseEnter={(o: any) => setFoco(o.dataKey)}
              onMouseLeave={() => setFoco(null)}
              formatter={(v, e: any) => (
                <span
                  style={{
                    color: colorPalette[chaves.indexOf(e.dataKey) % colorPalette.length],
                    opacity: !foco || foco === e.dataKey ? 1 : 0.2,
                    fontWeight: foco === e.dataKey ? 'bold' : 'normal',
                  }}
                >
                  {v}
                </span>
              )}
            />
            {chaves.map((chave, i) => (
              <Line
                key={chave}
                type="monotone"
                dataKey={chave}
                name={humanizarChave(chave)}
                stroke={colorPalette[i % colorPalette.length]}
                strokeOpacity={!foco || foco === chave ? 1 : 0.2}
                strokeDasharray={!foco || foco === chave ? '0' : '5 5'}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </HBox>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {seletorPeriodo}
      {conteudo}
    </div>
  );
};
